/**
 * Small hooks shared by every CRUD section of the Geography tab (WPS-222):
 * paging + search + facet state, and an `onSave` wrapper that turns the API's
 * per-field errors (a 409 on a natural-key conflict, a 400 on a bad date) into
 * inline field errors on the `EditFormDialog` instead of a toast only.
 */

import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { ProductFilterValue } from '@/shared/components/ui/product-filter-menu'
import type { EditSaveHelpers } from '@/admin/components/shared/editable-card-types'
import { fieldErrorsFromApiError } from '@/admin/pages/settings/member-attribute-helpers'
import { toastApiError } from '@/shared/lib/api-error'

import { DEFAULT_PAGE_SIZE } from './geography-utils'

export function useListState() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearchState] = useState('')
  const [filterValues, setFilterValuesState] = useState<ProductFilterValue>({})

  const setSearch = useCallback((value: string) => {
    setSearchState(value)
    setPage(1)
  }, [])
  const setFilterValues = useCallback((value: ProductFilterValue) => {
    setFilterValuesState(value)
    setPage(1)
  }, [])
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size)
    setPage(1)
  }, [])

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, page_size: pageSize }
    if (search.trim()) p.search = search.trim()
    for (const [key, values] of Object.entries(filterValues)) {
      if (values?.length) p[key] = values.join(',')
    }
    return p
  }, [page, pageSize, search, filterValues])

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filterValues,
    setFilterValues,
    params,
  }
}

/**
 * Run a save, surfacing API field errors inline. Rethrows after setting the
 * field errors so `EditFormDialog` keeps the dialog open on the failing field.
 */
export async function saveWithFieldErrors(
  run: () => Promise<unknown>,
  helpers: EditSaveHelpers | undefined,
  successMessage: string,
  failureMessage: string,
): Promise<void> {
  try {
    await run()
    toast.success(successMessage)
  } catch (err) {
    const fieldErrors = fieldErrorsFromApiError(err)
    const keys = Object.keys(fieldErrors)
    if (helpers && keys.length) {
      for (const [field, message] of Object.entries(fieldErrors)) {
        helpers.setFieldError(field, message)
      }
    } else {
      toastApiError(err, failureMessage)
    }
    throw err
  }
}

export const NONE = '__none__'

export function nullable(value: unknown): string | null {
  const s = value === undefined || value === null ? '' : String(value)
  return s && s !== NONE ? s : null
}
